export const COGNITO_TOKEN_STORAGE_KEY = "adminCognitoTokens";
export const COGNITO_PKCE_STORAGE_KEY = "adminCognitoPkce";
export const DEFAULT_ADMIN_GROUPS = ["admins", "super-admins"];

export type CognitoTokenSession = {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt: number;
};

export type StoredPkceState = {
  state: string;
  codeVerifier: string;
  redirectUri: string;
};

export const shouldClearPkceOnInitialSessionLoad = (pathname: string): boolean => {
  return pathname !== "/auth/callback";
};

const decodeBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  if (typeof window !== "undefined" && typeof window.atob === "function") {
    return window.atob(padded);
  }
  return Buffer.from(padded, "base64").toString("utf8");
};

export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const normalizeBaseUrl = (hostedUiBaseUrl: string): string => hostedUiBaseUrl.replace(/\/+$/, "");

export const buildAuthorizeUrl = ({
  hostedUiBaseUrl,
  clientId,
  redirectUri,
  state,
  codeChallenge,
  scope = "openid email profile",
  prompt = "login",
}: {
  hostedUiBaseUrl: string;
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  scope?: string;
  prompt?: "login" | "none" | "select_account";
}): string => {
  const url = new URL(`${normalizeBaseUrl(hostedUiBaseUrl)}/oauth2/authorize`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", scope);
  url.searchParams.set("prompt", prompt);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
};

export const buildTokenExchangeBody = ({
  clientId,
  code,
  codeVerifier,
  redirectUri,
}: {
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
}): URLSearchParams => {
  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("client_id", clientId);
  body.set("code", code);
  body.set("code_verifier", codeVerifier);
  body.set("redirect_uri", redirectUri);
  return body;
};

export const buildRefreshTokenBody = ({
  clientId,
  refreshToken,
}: {
  clientId: string;
  refreshToken: string;
}): URLSearchParams => {
  const body = new URLSearchParams();
  body.set("grant_type", "refresh_token");
  body.set("client_id", clientId);
  body.set("refresh_token", refreshToken);
  return body;
};

export const buildLogoutUrl = ({
  hostedUiBaseUrl,
  clientId,
  logoutUri,
}: {
  hostedUiBaseUrl: string;
  clientId: string;
  logoutUri: string;
}): string => {
  const url = new URL(`${normalizeBaseUrl(hostedUiBaseUrl)}/logout`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("logout_uri", logoutUri);
  return url.toString();
};

export const getCognitoGroups = (token: string): string[] => {
  const payload = decodeJwtPayload(token);
  const groups = payload?.["cognito:groups"];
  if (Array.isArray(groups)) return groups.filter((group): group is string => typeof group === "string");
  if (typeof groups === "string") return [groups];
  return [];
};

export const isAdminAccessToken = (
  token: string,
  adminGroups: string[] = DEFAULT_ADMIN_GROUPS,
): boolean => {
  const tokenGroups = new Set(getCognitoGroups(token));
  return adminGroups.some((group) => tokenGroups.has(group));
};

export const getTokenExpiresAt = (token: string): number | null => {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
};

export const createStoredTokenSession = ({
  accessToken,
  idToken,
  refreshToken,
  expiresIn,
}: {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
}): CognitoTokenSession => {
  const expiresAt = getTokenExpiresAt(accessToken) ?? Date.now() + Number(expiresIn || 0) * 1000;
  return {
    accessToken,
    idToken,
    refreshToken,
    expiresAt,
  };
};
